import { ReactElement, useState } from 'react';
import {
    UIIcon,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISpinner,
    UIText
} from '@/ui';
import { useTestCallDeviceAction } from '../actions';
import { useTestDeviceStatus } from '../queries';
import { DeviceTestCallStatusData, DeviceTestSmsStatusData } from '@/api';
import { blue, red } from '@/ui/styles';
import { useDevicesTranslation } from '../hooks';
import { TFunction } from 'i18next';

type CallStatus = DeviceTestCallStatusData['status'];
type SmsStatus = DeviceTestSmsStatusData['status'];

const CALL_SUCCESS: NonNullable<CallStatus>[] = ['completed'];
const CALL_FAILURE: NonNullable<CallStatus>[] = [
    'busy',
    'no-answer',
    'failed',
    'canceled'
];

const SMS_SUCCESS: NonNullable<SmsStatus>[] = [
    'delivered',
    'response_received'
];
const SMS_FAILURE: NonNullable<SmsStatus>[] = [
    'undelivered',
    'failed',
    'canceled',
    'response_timed_out'
];

function getCallLabel(t: TFunction, status: CallStatus): string {
    const labels: Record<NonNullable<CallStatus>, string> = {
        queued: t('components.test_call_device_modal.connecting'),
        ringing: t('components.test_call_device_modal.call_status_ringing'),
        'in-progress': t(
            'components.test_call_device_modal.call_status_in_progress'
        ),
        completed: t('components.test_call_device_modal.call_status_completed'),
        busy: t('components.test_call_device_modal.call_status_busy'),
        'no-answer': t(
            'components.test_call_device_modal.call_status_no_answer'
        ),
        failed: t('components.test_call_device_modal.call_status_failed'),
        canceled: t('components.test_call_device_modal.call_status_canceled')
    };
    if (status === null)
        return t('components.test_call_device_modal.connecting');
    return labels[status];
}

function getSmsLabel(t: TFunction, status: SmsStatus): string {
    const labels: Partial<Record<NonNullable<SmsStatus>, string>> = {
        queued: t('components.test_call_device_modal.sms_status_queued'),
        sending: t('components.test_call_device_modal.sms_status_sending'),
        sent: t('components.test_call_device_modal.sms_status_sent'),
        delivered: t('components.test_call_device_modal.sms_status_delivered'),
        undelivered: t(
            'components.test_call_device_modal.sms_status_undelivered'
        ),
        failed: t('components.test_call_device_modal.sms_status_failed'),
        canceled: t('components.test_call_device_modal.sms_status_canceled'),
        response_pending: t(
            'components.test_call_device_modal.sms_status_response_pending'
        ),
        response_received: t(
            'components.test_call_device_modal.sms_status_response_received'
        ),
        response_timed_out: t(
            'components.test_call_device_modal.sms_status_response_timed_out'
        )
    };
    if (status === null)
        return t('components.test_call_device_modal.connecting');
    return (
        labels[status] ??
        t('components.test_call_device_modal.sms_status_unknown')
    );
}

type StatusRowState = 'loading' | 'success' | 'failure';

function getCallState(status: CallStatus): StatusRowState {
    if (status !== null && CALL_SUCCESS.includes(status)) return 'success';
    if (status !== null && CALL_FAILURE.includes(status)) return 'failure';
    return 'loading';
}

function getSmsState(status: SmsStatus): StatusRowState {
    if (status !== null && SMS_SUCCESS.includes(status)) return 'success';
    if (status !== null && SMS_FAILURE.includes(status)) return 'failure';
    return 'loading';
}

function StatusRow(props: {
    label: string;
    state: StatusRowState;
    statusText: string;
}): ReactElement {
    const { label, state, statusText } = props;
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '6px 0'
            }}
        >
            <UIText type="default" style={{ fontWeight: 500 }}>
                {label}
            </UIText>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {state === 'loading' && <UISpinner />}
                {state === 'success' && (
                    <UIIcon name="check-circle" color={blue} size={18} />
                )}
                {state === 'failure' && (
                    <UIIcon name="alert-triangle" color={red} size={18} />
                )}
                <UIText
                    type="default"
                    style={{ color: state === 'failure' ? red : undefined }}
                >
                    {statusText}
                </UIText>
            </div>
        </div>
    );
}

type TestCallDeviceModalProps = {
    deviceId: string;
    isOpen: boolean;
    onClose: () => void;
};

export function TestCallDeviceModal(
    props: TestCallDeviceModalProps
): ReactElement {
    const { deviceId, isOpen, onClose } = props;
    const { t } = useDevicesTranslation();

    const [statusId, setStatusId] = useState<string | null>(null);

    const { testCallDevice, loading } = useTestCallDeviceAction({ deviceId });
    const { status, error: pollError } = useTestDeviceStatus({
        deviceId,
        statusId
    });

    function handleTest() {
        testCallDevice()
            .then((id) => {
                setStatusId(id);
            })
            .catch(() => {}); // Error handled in the action
    }

    function handleClose() {
        setStatusId(null);
        onClose();
    }

    function renderStatusContent(): ReactElement {
        if (pollError) {
            return (
                <UIText type="default" style={{ color: red }}>
                    {pollError}
                </UIText>
            );
        }

        const callStatus = status?.call.status ?? null;
        const smsStatus = status?.sms.status ?? null;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <StatusRow
                    label={t('components.test_call_device_modal.label_call')}
                    state={getCallState(callStatus)}
                    statusText={getCallLabel(t, callStatus)}
                />
                <StatusRow
                    label={t('components.test_call_device_modal.label_sms')}
                    state={getSmsState(smsStatus)}
                    statusText={getSmsLabel(t, smsStatus)}
                />
            </div>
        );
    }

    const isPolling = statusId !== null;

    return (
        <UIModal isOpen={isOpen} onClose={handleClose}>
            <UIModalHeader
                title={t('components.test_call_device_modal.title')}
            />
            <UIModalContent>
                {isPolling ? (
                    renderStatusContent()
                ) : (
                    <UIText type="default">
                        {t('components.test_call_device_modal.description')}
                    </UIText>
                )}
            </UIModalContent>
            <UIModalFooter
                actions={
                    isPolling
                        ? [
                              {
                                  text: t('shared.labels.close'),
                                  onClick: handleClose
                              }
                          ]
                        : [
                              {
                                  variant: 'primary',
                                  loading: loading,
                                  disabled: loading,
                                  text: t('shared.labels.test'),
                                  onClick: handleTest
                              },
                              {
                                  text: t('shared.labels.cancel'),
                                  disabled: loading,
                                  onClick: handleClose
                              }
                          ]
                }
            />
        </UIModal>
    );
}
